import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SpinnerCenter } from "@/components/Spinner";
import { EmptyState } from "./EmptyState";
import { cn } from "@/lib/utils";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";

export type Column<T> = {
  key: string;
  header: string;
  sortable?: boolean;
  className?: string;
  render: (row: T) => React.ReactNode;
};

type DataTableProps<T> = {
  columns: Column<T>[];
  data: T[];
  isLoading?: boolean;
  emptyMessage?: string;
  orderBy?: string;
  orderDir?: "asc" | "desc";
  onSort?: (key: string) => void;
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  /** Opts a specific row out of onRowClick's pointer/hover affordance and click handling — e.g. a synthetic totals row. Defaults to clickable when onRowClick is set. */
  isRowClickable?: (row: T) => boolean;
};

export function DataTable<T>({
  columns,
  data,
  isLoading,
  emptyMessage = "No results found.",
  orderBy,
  orderDir,
  onSort,
  rowKey,
  onRowClick,
  isRowClickable,
}: DataTableProps<T>) {
  if (isLoading) return <SpinnerCenter />;

  return (
    <div className="rounded-md border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((col) => (
              <TableHead
                key={col.key}
                className={cn(
                  col.sortable && onSort && "cursor-pointer select-none",
                  col.className
                )}
                onClick={col.sortable && onSort ? () => onSort(col.key) : undefined}
              >
                <div className="flex items-center gap-1">
                  {col.header}
                  {col.sortable && onSort && (
                    <span className="text-muted-foreground">
                      {orderBy === col.key ? (
                        orderDir === "asc" ? (
                          <ArrowUp className="size-3.5" />
                        ) : (
                          <ArrowDown className="size-3.5" />
                        )
                      ) : (
                        <ArrowUpDown className="size-3.5 opacity-40" />
                      )}
                    </span>
                  )}
                </div>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length}>
                <EmptyState title={emptyMessage} className="py-10" />
              </TableCell>
            </TableRow>
          ) : (
            data.map((row) => {
              const clickable = !!onRowClick && (isRowClickable ? isRowClickable(row) : true);
              return (
                <TableRow
                  key={rowKey(row)}
                  className={cn(clickable && "cursor-pointer hover:bg-muted/50")}
                  onClick={clickable ? () => onRowClick(row) : undefined}
                >
                  {columns.map((col) => (
                    <TableCell key={col.key} className={col.className}>
                      {col.render(row)}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}
